import React, { useState, useEffect, useRef, useCallback } from 'react';

const CLARITY_STATES = [
  { min: 70, label: 'Clear',  cls: 'good',  tip: 'Tap to capture' },
  { min: 40, label: 'Adjust', cls: 'warn',  tip: 'Adjust angle'   },
  { min: 0,  label: 'Blurry', cls: 'bad',   tip: 'Too blurry'     },
];

function clarityState(pct) {
  return CLARITY_STATES.find(s => pct >= s.min);
}

const JOB_TYPES = [
  { key: 'proxy',      label: 'Proxy' },
  { key: 'ca',         label: 'Corporate Action' },
  { key: 'regulatory', label: 'Regulatory' },
  { key: 'issuer',     label: 'Issuer' },
];

const badgeColors = { good: 'rgba(15,110,86,.9)', warn: 'rgba(146,64,14,.9)', bad: 'rgba(231,0,11,.8)' };
const fillColors  = { good: '#14b8a6', warn: '#d97706', bad: '#e7000b' };

function CameraViewfinder({ clarity, cs, canCapture, onCapture }) {
  return (
    <>
      <div className="relative bg-[#1a1a1a] mx-5 mt-4 rounded-xl overflow-hidden" style={{ aspectRatio: '4/3' }}>
        <div className="absolute inset-4 bg-white rounded opacity-90 flex items-center justify-center">
          <div style={{ fontFamily: 'Georgia, serif', fontSize: 9, color: '#333', padding: '12px', lineHeight: 1.6, maxWidth: 200 }}>
            <div style={{ fontWeight: 700, fontSize: 11, marginBottom: 8, textAlign: 'center', letterSpacing: 1 }}>TAKE PHOTO</div>
            <p>Align the document within the frame.</p>
          </div>
        </div>
        {([['top-2 left-2', 0], ['top-2 right-2', 1], ['bottom-2 left-2', 2], ['bottom-2 right-2', 3]] as [string, number][]).map(([pos, i]) => (
          <div
            key={i}
            className={`absolute ${pos} w-5 h-5`}
            style={{
              borderTop:    i < 2 ? '2px solid white' : undefined,
              borderBottom: i >= 2 ? '2px solid white' : undefined,
              borderLeft:   (i === 0 || i === 2) ? '2px solid white' : undefined,
              borderRight:  (i === 1 || i === 3) ? '2px solid white' : undefined,
              borderRadius: i === 0 ? '4px 0 0 0' : i === 1 ? '0 4px 0 0' : i === 2 ? '0 0 0 4px' : '0 0 4px 0',
            }}
          />
        ))}
        <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-semibold text-white" style={{ background: badgeColors[cs.cls] }}>
          {cs.label}
        </div>
        <div className="absolute bottom-3 left-3" style={{ width: 120 }}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-white opacity-80">Clarity</span>
            <span className="text-xs text-white font-semibold">{clarity}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/30 overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${clarity}%`, background: fillColors[cs.cls] }} />
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center py-4 gap-1">
        <button
          onClick={onCapture}
          disabled={!canCapture}
          className="w-14 h-14 rounded-full flex items-center justify-center transition-all active:scale-90"
          style={{
            background: canCapture ? '#14b8a6' : '#9CA3AF',
            opacity: canCapture ? 1 : 0.5,
            boxShadow: canCapture ? '0 0 0 4px rgba(20,184,166,.2)' : 'none',
          }}
        >
          <div className="w-5 h-5 rounded-full bg-white" />
        </button>
        <span className="text-xs text-muted">{cs.tip}</span>
      </div>
    </>
  );
}

function ThumbnailStrip({ captures, onRemove, label }) {
  if (captures.length === 0) return null;
  return (
    <div className="px-5 pb-3">
      <div className="text-xs font-medium text-copy uppercase tracking-wide mb-2">{label} ({captures.length})</div>
      <div className="flex gap-2 flex-wrap">
        {captures.map((c, i) => (
          <div key={c.id} className="relative w-14" style={{ height: 68 }}>
            <div
              className="w-full h-full rounded-lg border-2 flex items-center justify-center bg-wash text-xs font-bold text-copy"
              style={{ borderColor: c.clarity >= 70 ? '#14b8a6' : '#e7000b' }}
            >
              P{i + 1}
            </div>
            <button
              onClick={() => onRemove(c.id)}
              className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#e7000b] text-white flex items-center justify-center"
              style={{ fontSize: 9 }}
            >×</button>
            <div className="absolute bottom-1 right-1 w-3 h-3 rounded-full" style={{ background: c.clarity >= 70 ? '#14b8a6' : '#e7000b' }} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function CameraModal({ onClose, onFindJob, onSaveAndNext }) {
  const [clarity, setClarity]             = useState(88);
  const [phase, setPhase]                 = useState('jobTag'); // 'jobTag' | 'materialScans'
  const [jobTagCaptures, setJobTagCaps]   = useState([]);
  const [jobType, setJobType]             = useState(null);     // 'proxy' | 'ca' | 'regulatory' | 'issuer'
  const [materialCaptures, setMatCaps]    = useState([]);
  const intervalRef                       = useRef(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setClarity(prev => {
        const delta = (Math.random() - 0.48) * 18;
        return Math.max(5, Math.min(99, Math.round(prev + delta)));
      });
    }, 900);
    return () => clearInterval(intervalRef.current);
  }, []);

  const cs = clarityState(clarity);
  const canCapture = clarity >= 70;

  const captureJobTag = useCallback(() => {
    if (!canCapture) return;
    setJobTagCaps(prev => [...prev, { id: Date.now(), clarity }]);
  }, [canCapture, clarity]);

  const removeJobTag = useCallback((id) => {
    setJobTagCaps(prev => prev.filter(c => c.id !== id));
  }, []);

  const captureMaterial = useCallback(() => {
    if (!canCapture) return;
    setMatCaps(prev => [...prev, { id: Date.now(), clarity }]);
  }, [canCapture, clarity]);

  const removeMaterial = useCallback((id) => {
    setMatCaps(prev => prev.filter(c => c.id !== id));
  }, []);

  const goToStep2 = useCallback(() => {
    if (jobTagCaptures.length === 0 || !jobType) return;
    setPhase('materialScans');
  }, [jobTagCaptures.length, jobType]);

  const goBack = useCallback(() => {
    setPhase('jobTag');
  }, []);

  const handleFindJob = useCallback(() => {
    if (materialCaptures.length === 0) return;
    onFindJob?.({ jobType, jobTagCaptures, materialCaptures });
  }, [materialCaptures, jobType, jobTagCaptures, onFindJob]);

  const handleSaveAndNext = useCallback(() => {
    if (materialCaptures.length === 0) return;
    onSaveAndNext?.({ jobType, jobTagCaptures, materialCaptures });
    setPhase('jobTag');
    setJobTagCaps([]);
    setJobType(null);
    setMatCaps([]);
  }, [materialCaptures, jobType, jobTagCaptures, onSaveAndNext]);

  const step1Ready = jobTagCaptures.length >= 1 && jobType !== null;
  const step2Ready = materialCaptures.length >= 1;

  const step1Count = jobTagCaptures.length === 0
    ? 'No captures yet'
    : `${jobTagCaptures.length} capture(s)`;

  const step2Count = materialCaptures.length === 0
    ? 'No captures yet'
    : `${materialCaptures.length} scan(s) captured`;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center"
      style={{ background: 'rgba(4,50,67,.6)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-white rounded-2xl shadow-modal flex flex-col overflow-hidden"
        style={{ width: 480, maxHeight: '92vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-trim flex-shrink-0">
          <div>
            <div className="text-base font-semibold text-ink">Camera capture</div>
            <div className="text-xs text-muted">
              {phase === 'jobTag'        && 'Step 1 of 2: Capture Job Tag'}
              {phase === 'materialScans' && 'Step 2 of 2: Capture Material'}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-copy hover:bg-wash transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          </button>
        </div>

        {/* Step progress indicator — same 2-step flow for every job type. */}
        <div className="px-5 pt-3 pb-1 flex gap-2">
          {[1, 2].map(n => {
            const phaseForStep = { 1: 'jobTag', 2: 'materialScans' }[n];
            const stepIndex = (p) => ({ jobTag: 0, materialScans: 1 })[p];
            const active = phase === phaseForStep;
            const done   = stepIndex(phase) > stepIndex(phaseForStep);
            return (
              <div key={n} className="flex items-center gap-1.5 flex-1">
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                  style={{
                    background: done ? '#14b8a6' : active ? '#0f6e56' : '#E5E7EB',
                    color: (active || done) ? '#fff' : '#9CA3AF',
                  }}
                >
                  {done ? '✓' : n}
                </div>
                <div className="h-0.5 flex-1 rounded-full" style={{ background: done ? '#14b8a6' : '#E5E7EB' }} />
              </div>
            );
          })}
          <div
            className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
            style={{ background: '#E5E7EB', color: '#9CA3AF' }}
          >
            ✓
          </div>
        </div>

        {/* ── STEP 1: CAPTURE JOB TAG ── */}
        {phase === 'jobTag' && (
          <>
            <CameraViewfinder
              clarity={clarity}
              cs={cs}
              canCapture={canCapture}
              onCapture={captureJobTag}
            />

            <ThumbnailStrip
              captures={jobTagCaptures}
              onRemove={removeJobTag}
              label="Job Tag"
            />

            {/* Job type selector */}
            <div className="px-5 pb-3">
              <div className="text-xs font-medium text-copy uppercase tracking-wide mb-2">Tag as</div>
              <div className="flex gap-2">
                {JOB_TYPES.map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setJobType(jobType === key ? null : key)}
                    className="flex-1 py-2 px-3 rounded-lg border-2 text-xs font-medium transition-all"
                    style={{
                      borderColor: jobType === key ? '#14b8a6' : '#E5E7EB',
                      background:  jobType === key ? '#E9F6F6' : '#fff',
                      color:       jobType === key ? '#0f6e56' : '#595A60',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="px-5 pb-5 pt-2 border-t border-trim flex items-center justify-between gap-3">
              <span className="text-xs text-muted flex-1">{step1Count}</span>
              <button onClick={onClose} className="px-4 py-2 rounded-lg border border-trim text-sm font-medium text-copy hover:bg-wash transition-colors">
                Cancel
              </button>
              <button
                onClick={goToStep2}
                disabled={!step1Ready}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-all bg-gradient-to-r from-teal-500 to-cyan-500"
                style={{ opacity: step1Ready ? 1 : 0.5 }}
              >
                Next
              </button>
            </div>
          </>
        )}

        {/* ── STEP 2: CAPTURE MATERIAL SCANS ── */}
        {phase === 'materialScans' && (
          <>
            <CameraViewfinder
              clarity={clarity}
              cs={cs}
              canCapture={canCapture}
              onCapture={captureMaterial}
            />

            <ThumbnailStrip
              captures={materialCaptures}
              onRemove={removeMaterial}
              label="Material Scans"
            />

            <div className="px-5 pb-5 pt-2 border-t border-trim flex items-center justify-between gap-3">
              <span className="text-xs text-muted flex-1">{step2Count}</span>
              <button onClick={onClose} className="px-4 py-2 rounded-lg border border-trim text-sm font-medium text-copy hover:bg-wash transition-colors">
                Cancel
              </button>
              <button onClick={goBack} className="px-4 py-2 rounded-lg border border-trim text-sm font-medium text-copy hover:bg-wash transition-colors">
                Back
              </button>
              <button
                onClick={handleFindJob}
                disabled={!step2Ready}
                className="px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-all bg-gradient-to-r from-teal-500 to-cyan-500"
                style={{ opacity: step2Ready ? 1 : 0.5 }}
              >
                Find Job
              </button>
              <button
                onClick={handleSaveAndNext}
                disabled={!step2Ready}
                className="px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-all bg-brand-purple"
                style={{ opacity: step2Ready ? 1 : 0.5 }}
              >
                Save & Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
